import { useState, useRef, useMemo, useEffect } from 'react'
import { Box, Flex, VStack, Textarea } from '@chakra-ui/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toaster, Text, Modal, useT } from '@minetrace/ui'
import {
  useFormColors,
  FormSection,
  LabeledInput,
  LabeledDatePicker,
  LabeledTextarea,
  useForm,
} from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { LuPaperclip, LuTrash2, LuArrowLeft, LuFileText } from 'react-icons/lu'
import { brand, themeColors } from '../../theme'
import { settlementsService } from '../../api/services/settlements.service'
import type { ShipmentForSettlementResponse } from '../../api/models/response'
import { parseUtcDate } from '../../lib/dates'

// Only EnAnalisis — plant and lab are done, now the shipment is ready for settlement.
const VALID_STATES = [6]

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string | null) {
  if (!iso) return '-'
  const d = parseUtcDate(iso)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatWeight(v: number | null | undefined) {
  if (v == null) return '-'
  return `${v.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} t`
}

function formatNumber(v: number | null | undefined, digits = 4) {
  if (v == null) return '-'
  return v.toLocaleString('es-PE', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

interface SettlementFormValues {
  settlementDate: string
  plantWeight: string
  mineralGrade: string
  purchasePrice: string
  comments: string
}

export default function NuevaLiquidacionPage() {
  const colors = themeColors
  const c = useFormColors()
  const { t } = useT()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const batchCode = searchParams.get('batchCode') ?? ''
  const [searching, setSearching] = useState(false)
  const [shipment, setShipment] = useState<ShipmentForSettlementResponse | null>(null)
  const [notFound, setNotFound] = useState(false)

  const [files, setFiles] = useState<File[]>([])
  const [saving, setSaving] = useState(false)
  const [rejecting, setRejecting] = useState(false)

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const methods = useForm<SettlementFormValues>({
    defaultValues: {
      settlementDate: '',
      plantWeight: '',
      mineralGrade: '',
      purchasePrice: '',
      comments: '',
    },
  })

  const { watch, handleSubmit, reset } = methods
  const plantWeight = watch('plantWeight')
  const purchasePrice = watch('purchasePrice')

  const totalCost = useMemo(() => {
    const pw = parseFloat(plantWeight)
    const pp = parseFloat(purchasePrice)
    if (isNaN(pw) || isNaN(pp)) return null
    return pw * pp
  }, [plantWeight, purchasePrice])

  // Pre-fill plant weight and mineral grade from plant analysis.
  useEffect(() => {
    if (shipment) {
      reset({
        settlementDate: '',
        plantWeight: shipment.netWeight != null ? shipment.netWeight.toString() : '',
        mineralGrade: shipment.mineralGrade != null ? shipment.mineralGrade.toString() : '',
        purchasePrice: '',
        comments: '',
      })
    }
  }, [shipment, reset])

  // Auto-load the shipment when arriving via the three-dot menu on /liquidaciones.
  // If we land here without a batch code (old deep links, manual URL), bounce back to the list.
  useEffect(() => {
    const code = batchCode.trim()
    if (!code) {
      navigate('/app/cierre/liquidaciones', { replace: true })
      return
    }

    let cancelled = false
    const run = async () => {
      setSearching(true)
      setNotFound(false)
      try {
        const result = await settlementsService.lookup(code)
        if (cancelled) return
        if (!VALID_STATES.includes(result.shipmentState)) {
          toaster.error({ description: t('liquidaciones.nuevo.errors.invalidState') })
          setShipment(null)
          setNotFound(true)
        } else {
          setShipment(result)
        }
      } catch {
        if (cancelled) return
        toaster.error({ description: t('liquidaciones.nuevo.errors.notFound') })
        setShipment(null)
        setNotFound(true)
      } finally {
        if (!cancelled) setSearching(false)
      }
    }

    run()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchCode])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files)
      const emptyFile = newFiles.find((f) => f.size === 0)
      if (emptyFile) {
        toaster.error({ description: t('common.emptyFileError') })
      } else {
        setFiles((prev) => [...prev, ...newFiles])
      }
    }
    e.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const onSubmit = async (data: SettlementFormValues) => {
    if (!shipment) return

    if (!data.settlementDate) {
      toaster.error({ description: t('liquidaciones.nuevo.errors.dateRequired') })
      return
    }

    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('shipmentRecordId', shipment.id)
      formData.append('settlementDate', data.settlementDate)
      if (data.plantWeight) formData.append('plantWeight', data.plantWeight)
      if (data.mineralGrade) formData.append('mineralGrade', data.mineralGrade)
      if (data.purchasePrice) formData.append('purchasePrice', data.purchasePrice)
      if (data.comments) formData.append('comments', data.comments)
      files.forEach((file) => formData.append('documents', file))

      await settlementsService.create(formData)
      toaster.success({ description: t('liquidaciones.nuevo.toasts.created') })
      navigate('/app/cierre/liquidaciones')
    } catch {
      // Error toast handled by apiClient
    } finally {
      setSaving(false)
    }
  }

  const confirmReject = async () => {
    if (!shipment) return
    const reason = rejectReason.trim()
    if (!reason) {
      toaster.error({ description: 'Debe indicar el motivo del rechazo.' })
      return
    }

    setRejecting(true)
    try {
      await settlementsService.reject(shipment.id, reason)
      toaster.success({ description: 'Traslado rechazado.' })
      setRejectOpen(false)
      navigate('/app/cierre/liquidaciones')
    } catch {
      // handled by apiClient
    } finally {
      setRejecting(false)
    }
  }

  const labelStyle = {
    fontSize: 'sm' as const,
    fontWeight: '600' as const,
    color: c.textSecondary,
    minWidth: '220px',
  }

  const valueStyle = {
    fontSize: 'sm' as const,
    color: c.text,
  }

  return (
    <>
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
      style={{ display: 'none' }}
      onChange={handleFileChange}
    />
    <VStack gap={5} alignItems="stretch">
      {/* Header */}
      <Flex alignItems="center" gap={3}>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="36px"
          height="36px"
          borderRadius="lg"
          bg={colors.bgCard}
          cursor="pointer"
          _hover={{ bg: colors.bgHover }}
          onClick={() => navigate('/app/cierre/liquidaciones')}
        >
          <LuArrowLeft size={18} color={colors.textSecondary} />
        </Box>
        <Box>
          <Text tid="liquidaciones.nuevo.title" fontSize="xl" fontWeight="700" color={colors.text} />
          <Text tid="liquidaciones.nuevo.subtitle" fontSize="sm" color={colors.textSecondary} />
        </Box>
      </Flex>

      {/* Loading / not-found states */}
      {searching && (
        <Box bg={colors.bgCard} borderRadius="xl" border="1px solid" borderColor={colors.border} p={6}>
          <Text fontSize="sm" color={colors.textMuted}>
            Cargando traslado {batchCode}…
          </Text>
        </Box>
      )}

      {!searching && !shipment && notFound && (
        <Box bg={colors.bgCard} borderRadius="xl" border="1px solid" borderColor={colors.border} p={6}>
          <Text fontSize="sm" color={colors.text} mb={3}>
            No se pudo cargar el traslado <b>{batchCode}</b>. Puede que ya no esté en estado EnAnalisis.
          </Text>
          <Box
            as="button"
            type="button"
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={4}
            py={2}
            borderRadius="lg"
            border="1px solid"
            borderColor={colors.border}
            color={colors.text}
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
            _hover={{ bg: colors.bgHover }}
            onClick={() => navigate('/app/cierre/liquidaciones')}
          >
            Volver a la lista
          </Box>
        </Box>
      )}

      {/* Settlement Form */}
      {shipment && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={5} alignItems="stretch">
              {/* Read-only shipment data + plant analysis */}
              <FormSection icon={LuFileText} titleTid="liquidaciones.nuevo.sections.shipmentData" descriptionTid="liquidaciones.nuevo.sections.shipmentDataDescription">
                <VStack gap={3} alignItems="stretch">
                  <Flex gap={4}>
                    <Text tid="liquidaciones.nuevo.fields.supplier" {...labelStyle} />
                    <Text {...valueStyle}>{shipment.supplierRuc} - {shipment.supplierName || '-'}</Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text tid="liquidaciones.nuevo.fields.completedAt" {...labelStyle} />
                    <Text {...valueStyle}>{formatDate(shipment.startedAt)}</Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text tid="liquidaciones.nuevo.fields.batchCode" {...labelStyle} />
                    <Text {...valueStyle} fontFamily="monospace" fontWeight="600">
                      {shipment.batchCode}
                    </Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text tid="liquidaciones.nuevo.fields.mineral" {...labelStyle} />
                    <Text {...valueStyle} fontWeight="500" color={brand.green}>
                      {shipment.mineralTypeName || '-'}
                    </Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text tid="liquidaciones.nuevo.fields.originWeight" {...labelStyle} />
                    <Text {...valueStyle}>
                      {shipment.mineralWeight ? `${shipment.mineralWeight} ${shipment.measurementUnitName ?? ''}`.trim() : '-'}
                    </Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text {...labelStyle}>Peso bruto (planta)</Text>
                    <Text {...valueStyle}>{formatWeight(shipment.grossWeight)}</Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text {...labelStyle}>Peso neto (planta)</Text>
                    <Text {...valueStyle} fontWeight="600">{formatWeight(shipment.netWeight)}</Text>
                  </Flex>
                  <Flex gap={4}>
                    <Text {...labelStyle}>Humedad (%)</Text>
                    <Text {...valueStyle}>{formatNumber(shipment.moisture, 2)}</Text>
                  </Flex>
                </VStack>
              </FormSection>

              {/* Editable fields */}
              <FormSection icon={LuFileText} titleTid="liquidaciones.nuevo.sections.settlementData" descriptionTid="liquidaciones.nuevo.sections.settlementDataDescription">
                <VStack gap={4} alignItems="stretch" maxW="500px">
                  <LabeledDatePicker
                    label={t('liquidaciones.nuevo.fields.settlementDate')}
                    required
                    placeholder={t('liquidaciones.nuevo.fields.settlementDatePlaceholder')}
                    value={methods.watch('settlementDate')}
                    onChange={(val: string) => methods.setValue('settlementDate', val)}
                  />

                  <LabeledInput
                    label={t('liquidaciones.nuevo.fields.plantWeight')}
                    placeholder={t('liquidaciones.nuevo.fields.weightPlaceholder')}
                    type="number"
                    step="0.001"
                    {...methods.register('plantWeight')}
                  />

                  <LabeledInput
                    label={t('liquidaciones.nuevo.fields.mineralGrade')}
                    placeholder={t('liquidaciones.nuevo.fields.mineralGradePlaceholder')}
                    {...methods.register('mineralGrade')}
                  />

                  <LabeledInput
                    label={t('liquidaciones.nuevo.fields.purchasePrice')}
                    placeholder={t('liquidaciones.nuevo.fields.weightPlaceholder')}
                    type="number"
                    step="0.01"
                    {...methods.register('purchasePrice')}
                  />

                  <Box
                    p={4}
                    bg={totalCost != null ? `${brand.green}10` : c.bgInput}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor={totalCost != null ? `${brand.green}30` : c.border}
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text tid="liquidaciones.nuevo.fields.totalCost" fontSize="sm" fontWeight="600" color={c.textSecondary} />
                      <Text fontSize="lg" fontWeight="700" color={totalCost != null ? brand.green : c.textMuted} fontFamily="monospace">
                        {totalCost != null ? `S/ ${totalCost.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                      </Text>
                    </Flex>
                    {totalCost != null && (
                      <Text fontSize="xs" color={c.textMuted} mt={1}>
                        {t('liquidaciones.nuevo.fields.totalCostDetail', {
                          weight: plantWeight,
                          price: parseFloat(purchasePrice).toLocaleString('es-PE', { minimumFractionDigits: 2 }),
                        })}
                      </Text>
                    )}
                  </Box>

                  <LabeledTextarea
                    label={t('liquidaciones.nuevo.fields.comments')}
                    placeholder={t('liquidaciones.nuevo.fields.commentsPlaceholder')}
                    rows={3}
                    {...methods.register('comments')}
                  />
                </VStack>
              </FormSection>

              {/* File attachments */}
              <FormSection icon={LuPaperclip} titleTid="liquidaciones.nuevo.sections.documents" descriptionTid="liquidaciones.nuevo.sections.documentsDescription">
                <Box
                  as="button"
                  type="button"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  px={4}
                  py={2}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={brand.green}
                  color={brand.green}
                  fontWeight="600"
                  fontSize="sm"
                  cursor="pointer"
                  bg="transparent"
                  _hover={{ bg: `${brand.green}10` }}
                  mb={4}
                  onClick={() => {
                    fileInputRef.current?.click()
                  }}
                >
                  <LuPaperclip size={16} />
                  {t('liquidaciones.nuevo.attachButton')}
                </Box>
                {files.length > 0 ? (
                  <Box overflowX="auto" border="1px solid" borderColor={c.border} borderRadius="xl">
                    <Box as="table" width="100%" fontSize="sm">
                      <Box as="thead">
                        <Box as="tr" bg={c.bgInput}>
                          <Box as="th" textAlign="left" px={4} py={3} color={c.textSecondary} fontWeight="600" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" w="60px">
                            {t('liquidaciones.nuevo.columns.action')}
                          </Box>
                          <Box as="th" textAlign="left" px={4} py={3} color={c.textSecondary} fontWeight="600" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                            {t('liquidaciones.nuevo.columns.fileName')}
                          </Box>
                          <Box as="th" textAlign="right" px={4} py={3} color={c.textSecondary} fontWeight="600" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" w="100px">
                            {t('liquidaciones.nuevo.columns.fileSize')}
                          </Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        {files.map((file, index) => (
                          <Box as="tr" key={`${file.name}-${index}`} borderTop="1px solid" borderColor={c.border}>
                            <Box as="td" px={4} py={3}>
                              <Box
                                as="button"
                                type="button"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                width="28px"
                                height="28px"
                                borderRadius="md"
                                color="#c8442f"
                                cursor="pointer"
                                _hover={{ bg: 'rgba(200, 68, 47, 0.1)' }}
                                onClick={() => handleRemoveFile(index)}
                              >
                                <LuTrash2 size={14} />
                              </Box>
                            </Box>
                            <Box as="td" px={4} py={3}>
                              <Text color={c.text}>{file.name}</Text>
                            </Box>
                            <Box as="td" px={4} py={3} textAlign="right">
                              <Text color={c.textMuted}>{formatFileSize(file.size)}</Text>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box p={6} textAlign="center" border="1px dashed" borderColor={c.border} borderRadius="xl">
                    <Text tid="liquidaciones.nuevo.noDocuments" fontSize="sm" color={c.textMuted} />
                  </Box>
                )}
              </FormSection>

              {/* Action buttons */}
              <Flex gap={3} justifyContent="flex-end">
                <Box
                  as="button"
                  type="button"
                  px={6}
                  py={2.5}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colors.border}
                  color={colors.text}
                  fontWeight="600"
                  fontSize="sm"
                  cursor="pointer"
                  _hover={{ bg: colors.bgHover }}
                  onClick={() => navigate('/app/cierre/liquidaciones')}
                >
                  {t('liquidaciones.nuevo.actions.cancel')}
                </Box>
                <Box
                  as="button"
                  type="button"
                  px={6}
                  py={2.5}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="#c8442f"
                  color="#c8442f"
                  bg="transparent"
                  fontWeight="600"
                  fontSize="sm"
                  cursor="pointer"
                  _hover={{ bg: 'rgba(200, 68, 47, 0.08)' }}
                  onClick={() => {
                    setRejectReason('')
                    setRejectOpen(true)
                  }}
                >
                  Rechazar
                </Box>
                <Box
                  as="button"
                  type="submit"
                  px={6}
                  py={2.5}
                  borderRadius="lg"
                  bg={brand.green}
                  color="white"
                  fontWeight="600"
                  fontSize="sm"
                  cursor="pointer"
                  opacity={saving ? 0.7 : 1}
                  _hover={{ opacity: 0.9 }}
                >
                  {saving ? t('liquidaciones.nuevo.actions.saving') : t('liquidaciones.nuevo.actions.save')}
                </Box>
              </Flex>
            </VStack>
          </form>
        </FormProvider>
      )}
    </VStack>

    {/* Reject confirmation modal */}
    <Modal
      open={rejectOpen}
      onClose={() => (rejecting ? null : setRejectOpen(false))}
      title="Rechazar liquidación"
      subtitle={shipment ? `Lote ${shipment.batchCode}` : undefined}
      size="md"
    >
      <VStack gap={4} alignItems="stretch">
        <Text fontSize="sm" color={colors.textSecondary}>
          Esta acción marcará el traslado como <b>Rechazado</b> y finalizará el flujo. Se registrará el motivo en el expediente.
        </Text>
        <Box>
          <Text fontSize="sm" fontWeight="600" color={colors.text} mb={2}>
            Motivo del rechazo
          </Text>
          <Textarea
            placeholder="Describa el motivo del rechazo"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            bg={c.bgInput}
            borderColor={c.border}
            color={c.text}
          />
        </Box>
        <Flex gap={2} justifyContent="flex-end">
          <Box
            as="button"
            type="button"
            px={4}
            py={2}
            borderRadius="lg"
            border="1px solid"
            borderColor={colors.border}
            color={colors.text}
            fontWeight="600"
            fontSize="sm"
            cursor={rejecting ? 'not-allowed' : 'pointer'}
            _hover={{ bg: colors.bgHover }}
            onClick={() => setRejectOpen(false)}
            disabled={rejecting}
          >
            Cancelar
          </Box>
          <Box
            as="button"
            type="button"
            px={4}
            py={2}
            borderRadius="lg"
            bg="#c8442f"
            color="white"
            fontWeight="600"
            fontSize="sm"
            cursor={rejecting ? 'not-allowed' : 'pointer'}
            opacity={rejecting ? 0.7 : 1}
            _hover={{ opacity: 0.9 }}
            onClick={confirmReject}
            disabled={rejecting}
          >
            {rejecting ? 'Rechazando…' : 'Confirmar rechazo'}
          </Box>
        </Flex>
      </VStack>
    </Modal>
    </>
  )
}
